/**
 * Top-most overlay scene that hosts the single ProfileTooltipCard. Always
 * added to the game (Variant A simply never shows the card). ProfileTooltip-
 * Service drives `show` / `hide`; this scene owns positioning, the
 * click-outside catcher, and the Esc keybind.
 *
 * Why a scene (vs. a GameObject inside the host scene): the runner-badge
 * click path needs the tooltip to stay visible WHILE the runner is paused,
 * and a top-most scene is the cleanest way to keep it rendering and
 * receiving input while gameplay scenes are frozen.
 *
 * See `docs/superpowers/specs/2026-05-18-profile-hover-tooltip-design.md`.
 */

import Phaser from "phaser";

import { NATIVE_HEIGHT, NATIVE_WIDTH } from "../config";
import type { AuthorProfile } from "../types";

import type { AuthorIdentityService } from "../services/AuthorIdentityService";

import {
  PROFILE_CARD_HEIGHT,
  PROFILE_CARD_WIDTH,
  ProfileTooltipCard,
} from "../ui/ProfileTooltipCard";

const ANCHOR_GAP = 6;
const VIEWPORT_MARGIN = 2;
const SCENE_DEPTH = 1000;

/** Anchor rectangle in NATIVE screen space (top-left origin). */
export interface ProfileAnchor {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface ProfileShowRequest {
  profile: AuthorProfile;
  anchor: ProfileAnchor;
  /** When set, this scene is paused while the tooltip is open. */
  modalForSceneKey?: string;
  /** Pre-resolved (i18n-substituted) text the card renders verbatim. */
  strings: { stats: string; memberSince: string };
  /** Provided on first show so the card can lazy-build (the identity service
   *  isn't guaranteed to be in the registry when the scene first booted). */
  identityService: AuthorIdentityService;
  onDismiss: (reason: ProfileDismissReason) => void;
}

export type ProfileDismissReason = "mouse_leave" | "esc" | "outside_click" | "toggle" | "forced";

export class ProfileTooltipScene extends Phaser.Scene {
  static readonly KEY = "ProfileTooltipScene";

  private card?: ProfileTooltipCard;
  /** Full-screen invisible rect that swallows clicks while modal. */
  private outsideCatcher?: Phaser.GameObjects.Rectangle;
  private modalSceneKey?: string;
  private onDismiss?: (reason: ProfileDismissReason) => void;

  constructor() {
    super({ key: ProfileTooltipScene.KEY, active: true });
  }

  create(): void {
    // The card itself is lazy — built the first time `show` is called so we
    // don't depend on the AuthorIdentityService being registered at boot.
    this.outsideCatcher = this.add
      .rectangle(0, 0, NATIVE_WIDTH, NATIVE_HEIGHT, 0x000000, 0)
      .setOrigin(0, 0)
      .setDepth(SCENE_DEPTH);
    this.outsideCatcher.setVisible(false);
    this.outsideCatcher.setInteractive();
    this.outsideCatcher.on("pointerdown", () => this.handleDismiss("outside_click"));

    this.input.keyboard?.on("keydown-ESC", () => {
      if (this.card?.container.visible) this.handleDismiss("esc");
    });

    // Always sit on top of any other active scene.
    this.scene.bringToTop();
  }

  show(req: ProfileShowRequest): void {
    if (!this.card) {
      this.card = new ProfileTooltipCard(this, req.identityService);
      this.card.setDepth(SCENE_DEPTH + 1);
    }

    this.card.setProfile(req.profile, req.strings);
    const { x, y } = this.placeCard(req.anchor);
    this.card.setPosition(x, y);
    this.card.setVisible(true);

    if (req.modalForSceneKey) {
      this.modalSceneKey = req.modalForSceneKey;
      if (this.scene.isActive(req.modalForSceneKey) && !this.scene.isPaused(req.modalForSceneKey)) {
        this.scene.pause(req.modalForSceneKey);
      }
      this.outsideCatcher?.setVisible(true);
    } else {
      this.modalSceneKey = undefined;
      this.outsideCatcher?.setVisible(false);
    }
    this.scene.bringToTop();
    this.onDismiss = req.onDismiss;
  }

  hide(reason: ProfileDismissReason): void {
    if (!this.card?.container.visible) return;
    this.handleDismiss(reason);
  }

  /** Returns true if the card is currently shown. */
  isVisible(): boolean {
    return !!this.card?.container.visible;
  }

  private handleDismiss(reason: ProfileDismissReason): void {
    this.card?.setVisible(false);
    this.outsideCatcher?.setVisible(false);
    if (this.modalSceneKey && this.scene.isPaused(this.modalSceneKey)) {
      this.scene.resume(this.modalSceneKey);
    }
    this.modalSceneKey = undefined;
    const cb = this.onDismiss;
    this.onDismiss = undefined;
    cb?.(reason);
  }

  /**
   * Position the card next to its anchor. Default: 6 px above the anchor's
   * top edge, horizontally centred. If that would clip off the top of the
   * screen, flip below. Always clamp horizontally to the viewport.
   */
  private placeCard(anchor: ProfileAnchor): { x: number; y: number } {
    const w = PROFILE_CARD_WIDTH;
    const h = PROFILE_CARD_HEIGHT;
    const aboveY = anchor.y - h - ANCHOR_GAP;
    const belowY = anchor.y + anchor.height + ANCHOR_GAP;
    const y = aboveY >= VIEWPORT_MARGIN
      ? aboveY
      : belowY + h <= NATIVE_HEIGHT - VIEWPORT_MARGIN
        ? belowY
        : Math.max(VIEWPORT_MARGIN, NATIVE_HEIGHT - h - VIEWPORT_MARGIN);
    let x = Math.round(anchor.x + anchor.width / 2 - w / 2);
    if (x < VIEWPORT_MARGIN) x = VIEWPORT_MARGIN;
    if (x + w > NATIVE_WIDTH - VIEWPORT_MARGIN) x = NATIVE_WIDTH - w - VIEWPORT_MARGIN;
    return { x, y };
  }
}
